import { Box, LinearProgress, SxProps, Theme, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

interface PasswordStrengthIndicatorProps {
    password: string;
    sx?: SxProps<Theme>;
}

const PasswordStrengthIndicator = ({ password, sx }: PasswordStrengthIndicatorProps) => {
    const { t } = useTranslation();
    const [strength, setStrength] = useState(0);
    const [label, setLabel] = useState('');
    const [color, setColor] = useState<'error' | 'warning' | 'success' | 'info'>('error');

    useEffect(() => {
        if (!password) {
            setStrength(0);
            setLabel('');
            setColor('error');
            return;
        }

        // Calculate password strength
        let currentStrength = 0;

        // Length check
        if (password.length >= 8) currentStrength += 20;

        // Contains lowercase
        if (/[a-z]/.test(password)) currentStrength += 20;

        // Contains uppercase
        if (/[A-Z]/.test(password)) currentStrength += 20;

        // Contains number
        if (/[0-9]/.test(password)) currentStrength += 20;

        // Contains special character
        if (/[^A-Za-z0-9]/.test(password)) currentStrength += 20;

        setStrength(currentStrength);

        // Set appropriate label and color
        if (currentStrength < 40) {
            setLabel(t('auth.passwordStrength.weak'));
            setColor('error');
        } else if (currentStrength < 80) {
            setLabel(t('auth.passwordStrength.fair'));
            setColor('warning');
        } else {
            setLabel(t('auth.passwordStrength.strong'));
            setColor('success');
        }
    }, [password]);

    if (!password) return null;

    return (
        <Box sx={{ 
            width: '100%', 
            marginTop: '4px', 
            marginBottom: '16px',
            ...sx 
        }}>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '4px' 
            }}>
                <Typography variant="caption" color="text.secondary">
                    {t('auth.passwordStrength.label')}
                </Typography>
                <Typography variant="caption" color={color}>
                    {label}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={strength}
                color={color}
                sx={{ 
                    height: '6px', 
                    borderRadius: '3px' 
                }}
            />
        </Box>
    );
};

export default PasswordStrengthIndicator;
